import { fromBlob } from 'image-resize-compress';
import { forwardRef, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ReactLoading from 'react-loading';
import useSectionHighlightRef from '../../../hooks/useSectionHighlightRef';
import './ImageUpOrg.css';

const ImageUpOrg = forwardRef(({ imgData, setImgData, isEdit, videoFile, setVideoFile }, ref) => {
    const { sectionRef, highlightField } = useSectionHighlightRef(ref);
    const [draggedItem, setDraggedItem] = useState(null);
    const [loadingWallImgCount, setLoadingWallImgCount] = useState(0);
    const [loadingThumbImgCount, setLoadingThumbImgCount] = useState(0);
    const delay = (ms) => new Promise((res) => setTimeout(res, ms));
    const [videoPreview, setVideoPreview] = useState('');
    const [videoLoading, setVideoLoading] = useState(false);
    const videoInputRef = useRef(null);

    async function handelWallImage(e) {
        if (!e.target.files[0]) {
            setImgData((prevState) => ({ ...prevState, newWallImg: '' }));
            return;
        }

        const selectedFiles = Array.from(e.target.files);
        const sizeLimit = 2 * 1024 * 1024;

        const totalCount = (imgData.oldWallImg?.length || 0) + (imgData.newWallImg?.length || 0);
        if (selectedFiles.length + totalCount > 20) {
            toast.error('You can select only up to 20 images');
            return;
        }

        setLoadingWallImgCount(totalCount + selectedFiles.length);

        try {
            const compressedPromises = selectedFiles.map(async (file) => {
                await delay(2000);
                if (file.size <= sizeLimit) return file;

                const compressed = await fromBlob(file, 70, 'auto', 'auto');
                return new File([compressed], file.name, {
                    type: compressed.type,
                    lastModified: file.lastModified
                });
            });

            const compressedFiles = await Promise.all(compressedPromises);
            setImgData((prev) => ({
                ...prev,
                newWallImg: [...(prev.newWallImg || []), ...compressedFiles],
                oldWallImg: isEdit ? prev.oldWallImg : []
            }));
        } catch (error) {
            console.error('Error compressing images:', error);
            toast.error('Image compression failed. Please try again.');
        } finally {
            setLoadingWallImgCount(0);
        }
    }

    async function handelThumbImage(e) {
        if (!e.target.files[0]) {
            setImgData((prevState) => ({ ...prevState, newThumbImg: '' }));
            return;
        }

        const selectedFiles = Array.from(e.target.files);
        const sizeLimit = 500 * 1024;
        const totalCount = (imgData.oldThumbImg?.length || 0) + (imgData.newThumbImg?.length || 0);
        if (selectedFiles.length + totalCount > 5) {
            toast.error('You can select only up to 5 images');
            return;
        }

        setLoadingThumbImgCount(selectedFiles.length + totalCount);

        try {
            const compressedPromises = selectedFiles.map(async (file) => {
                await delay(2000);
                if (file.size <= sizeLimit) return file;

                const quality = 20;
                const width = 'auto';
                const height = 'auto';

                const compressed = await fromBlob(file, quality, width, height);

                const compressedFile = new File([compressed], file.name, {
                    type: compressed.type,
                    lastModified: file.lastModified
                });

                return compressedFile;
            });

            const compressedFiles = await Promise.all(compressedPromises);

            setImgData((prevState) => ({
                ...prevState,
                newThumbImg: [...(prevState.newThumbImg || []), ...compressedFiles],
                oldThumbImg: isEdit ? prevState.oldThumbImg : []
            }));
        } catch (error) {
            console.error('Error compressing thumbnails:', error);
            toast.error('Thumbnail compression failed. Please try again.');
        } finally {
            setLoadingThumbImgCount(0);
        }
    }

    const handleVideoChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            setVideoFile(null);
            setVideoPreview('');
            return;
        }

        const maxSizeMB = 30;
        if (file.size > maxSizeMB * 1024 * 1024) {
            toast.error(`File size exceeds ${maxSizeMB}MB`);
            if (videoPreview) URL.revokeObjectURL(videoPreview);
            setVideoFile(null);
            setVideoPreview('');
            if (videoInputRef.current) videoInputRef.current.value = '';
            return;
        }

        setVideoLoading(true);
        setTimeout(() => {
            if (videoPreview) URL.revokeObjectURL(videoPreview);
            setVideoFile(file);
            setVideoPreview(URL.createObjectURL(file));
            setVideoLoading(false);
        }, 1000);
    };

    const handleDragStart = (index, type) => {
        setDraggedItem({ index, type });
    };

    const handleDragOver = (e) => {
        e.preventDefault(); // Necessary to allow dropping
    };

    const handleDrop = (dropIndex, type) => {
        if (!draggedItem || draggedItem.type !== type || draggedItem.index === dropIndex) return;

        const imageKey = type === 'wall' ? 'newWallImg' : 'newThumbImg';
        const fallbackKey = type === 'wall' ? 'oldWallImg' : 'oldThumbImg';

        const currentImages = imgData?.[imageKey]?.length > 0 ? [...imgData[imageKey]] : [...imgData[fallbackKey]];

        const [movedImg] = currentImages.splice(draggedItem.index, 1);
        currentImages.splice(dropIndex, 0, movedImg);

        setImgData((prev) => ({
            ...prev,
            [imgData?.[imageKey]?.length > 0 ? imageKey : fallbackKey]: currentImages
        }));

        setDraggedItem(null);
    };

    const handleRemoveImage = (index, type = 'wall') => {
        const newKey = type === 'wall' ? 'newWallImg' : 'newThumbImg';
        const oldKey = type === 'wall' ? 'oldWallImg' : 'oldThumbImg';
        setImgData((prev) => {
            const newImages = [...(prev[newKey] || [])];
            const oldImages = [...(prev[oldKey] || [])];
            const combinedImages = [...oldImages, ...newImages];
            combinedImages.splice(index, 1);
            const updatedOld = combinedImages.filter((item) => typeof item === 'string');
            const updatedNew = combinedImages.filter((item) => item instanceof File);
            return {
                ...prev,
                [oldKey]: updatedOld,
                [newKey]: updatedNew
            };
        });
    };

    const disableDragAndDropWall = isEdit && imgData?.newWallImg?.length > 0;
    const disableDragAndDropThumb = isEdit && imgData?.newThumbImg?.length > 0;

    return (
        <div ref={sectionRef}>
            <h2 className='orgPackUpH2'>Images</h2>
            {/* image upload div */}
            <div className='orgPackUpSecDiv'>
                <div className='itineraryInDiv'>
                    <label className='itineraryLabel'>Wallpaper Images (upto 20)</label>
                    <div className='itineraryInputDiv'>
                        <input
                            type='file'
                            multiple
                            accept='.jpg, .jpeg, .png, .webp'
                            className='itineraryInput'
                            onChange={(e) => handelWallImage(e)}
                        />
                    </div>
                </div>
                {loadingWallImgCount > 0 ? (
                    <>
                        <p className='dragNote'>Note: Use drag and drop to reorder the Wallpaper images</p>
                        <div className='imgDisplayDiv'>
                            {Array.from({ length: loadingWallImgCount }).map((_, i) => (
                                <div className='imageSpinnerContainer' key={i}>
                                    <ReactLoading type='spin' color='#006aff' height={30} width={30} />
                                </div>
                            ))}
                        </div>
                    </>
                ) : imgData?.newWallImg?.length !== 0 || imgData?.oldWallImg?.length !== 0 ? (
                    <>
                        {!disableDragAndDropWall ? (
                            <p className='dragNote'>Note: Use drag and drop to reorder the Wallpaper images</p>
                        ) : (
                            <p className='dragNote'>
                                Note: Drag and drop is disabled in edit mode when new images are added.
                            </p>
                        )}
                        <div className='imgDisplayDiv'>
                            {(() => {
                                const wallImg = [...(imgData?.oldWallImg || []), ...(imgData?.newWallImg || [])];
                                return wallImg?.map((upImg, imgKey) => {
                                    // To display the image convert it to URL
                                    const fileURL = typeof upImg === 'string' ? upImg : URL.createObjectURL(upImg);
                                    return (
                                        <div className='imageContainer'>
                                            <img
                                                src={fileURL}
                                                alt='trvto_package'
                                                key={imgKey}
                                                className='wallImgDisp'
                                                loading='lazy'
                                                decoding='async'
                                                draggable={!disableDragAndDropWall}
                                                onDragStart={
                                                    !disableDragAndDropWall
                                                        ? () => handleDragStart(imgKey, 'wall')
                                                        : undefined
                                                }
                                                onDragOver={!disableDragAndDropWall ? handleDragOver : undefined}
                                                onDrop={
                                                    !disableDragAndDropWall
                                                        ? () => handleDrop(imgKey, 'wall')
                                                        : undefined
                                                }
                                            />
                                            <button onClick={() => handleRemoveImage(imgKey, 'wall')}>X</button>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </>
                ) : null}
                <div className='itineraryInDiv'>
                    {/* small image upload div */}
                    <label className='itineraryLabel'>Thumbnail Images (upto 6)</label>
                    <div className='itineraryInputDiv'>
                        <input
                            type='file'
                            multiple
                            accept='.jpg, .jpeg, .png, .webp'
                            className='itineraryInput'
                            onChange={(e) => handelThumbImage(e)}
                        />
                    </div>
                </div>
                {loadingThumbImgCount > 0 ? (
                    <>
                        <div className='imgDisplayDiv'>
                            {Array.from({ length: loadingThumbImgCount }).map((_, i) => (
                                <div className='imageSpinnerContainer' key={i}>
                                    <ReactLoading type='spin' color='#006aff' height={30} width={30} />
                                </div>
                            ))}
                        </div>
                    </>
                ) : (imgData?.newThumbImg?.length !== 0 || imgData?.oldThumbImg?.length) !== 0 ? (
                    <>
                        {!disableDragAndDropThumb && (
                            <p className='dragNote'>Note: Use drag and drop to reorder the Thumbnail images</p>
                        )}
                        <div className='imgDisplayDiv'>
                            {(() => {
                                const thumbImg = [...(imgData?.oldThumbImg || []), ...(imgData?.newThumbImg || [])];

                                return thumbImg?.map((upImg, imgKey) => {
                                    // To display the image convert it to URL
                                    const fileURL = typeof upImg === 'string' ? upImg : URL.createObjectURL(upImg);

                                    return (
                                        <div className='imageContainer'>
                                            <img
                                                src={fileURL}
                                                alt='trvto_package'
                                                key={imgKey}
                                                className='wallImgDisp'
                                                loading='lazy'
                                                decoding='async'
                                                draggable={!disableDragAndDropThumb}
                                                onDragStart={
                                                    !disableDragAndDropThumb
                                                        ? () => handleDragStart(imgKey, 'thumb')
                                                        : undefined
                                                }
                                                onDragOver={!disableDragAndDropThumb ? handleDragOver : undefined}
                                                onDrop={
                                                    !disableDragAndDropThumb
                                                        ? () => handleDrop(imgKey, 'thumb')
                                                        : undefined
                                                }
                                            />
                                            <button onClick={() => handleRemoveImage(imgKey, 'thumb')}>X</button>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </>
                ) : null}
                <div className='itineraryInDiv'>
                    <label className='itineraryLabel'>Upload Video</label>
                    <div className='itineraryInputDiv'>
                        <input
                            ref={videoInputRef}
                            type='file'
                            multiple
                            accept='video/mp4, video/webm, video/ogg'
                            className='itineraryInput'
                            onChange={(e) => handleVideoChange(e)}
                        />
                    </div>
                </div>
                {videoLoading && (
                    <div className='videoSpinnerContainer'>
                        <ReactLoading type='spin' color='#006aff' height={30} width={30} />
                    </div>
                )}
                {videoFile && (
                    <div className='videoContainer'>
                        <video
                            src={typeof videoFile === 'string' ? videoFile : videoPreview}
                            controls
                            width='400'
                            style={{ marginTop: '10px', borderRadius: '10px' }}
                        />
                        <button
                            onClick={() => {
                                setVideoFile('');
                                setVideoLoading(false);
                                if (videoInputRef.current) {
                                    videoInputRef.current.value = '';
                                }
                            }}
                        >
                            X
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
});

export default ImageUpOrg;
